import type {
  ApBillStatus,
  InboxDirection,
  InboxDocumentStatus,
  InboxDocumentType,
  InboxProcessingStatus,
} from '../generated/enums.js';

export type ReceivedNfDocumentTypeFilter = 'NF' | 'NFS' | InboxDocumentType;

export interface ReceivedNfListItemResponse {
  receivedNfId: string;
  entityId: string;
  /** Provider that put the row here: 'focusnfe' | 'fiscalapi' | seed/import sources. */
  source: string;
  /** ISO 4217 code when the document states one (CFDIs do); null = unknown, never assume BRL. */
  currency?: string | null;
  accessKey: string;
  documentType: InboxDocumentType;
  documentStatus: InboxDocumentStatus;
  focusStatusRaw?: string | null;
  documentNumber?: string | null;
  series?: string | null;
  issuerName?: string | null;
  issuerCnpj?: string | null;
  issuerUf?: string | null;
  issueDate?: string | null;
  competenceDate?: string | null;
  totalAmount?: number | null;
  direction?: InboxDirection | null;
  processingStatus: InboxProcessingStatus;
  linkedBillId?: string | null;
  /** Current AP lifecycle; independent from cancellation of the fiscal document. */
  linkedBillStatus?: ApBillStatus | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface ReceivedNfListResponse {
  data: ReceivedNfListItemResponse[];
  total: number;
  page: number;
  limit: number;
}

/** Alchemy status filter: pending (bruta), transmuted (has bill), removed (bill deleted) */
export type ReceivedNfAlchemyStatusFilter = 'pending' | 'transmuted' | 'removed';

export interface FindReceivedNfsQuery {
  entityId?: string;
  documentStatus?: InboxDocumentStatus;
  documentType?: ReceivedNfDocumentTypeFilter;
  focusStatuses?: string[];
  /** Filter by issuer CNPJ (digits only, partial match supported) */
  issuerCnpj?: string;
  /** Filter by alchemy/processing status (first column) */
  alchemyStatus?: ReceivedNfAlchemyStatusFilter;
  issueDateFrom?: string;
  issueDateTo?: string;
  totalAmount?: number;
  totalAmountOp?: 'eq' | 'gte' | 'lte';
  page?: number;
  limit?: number;
}

export interface ReceivedNfAddressingSummaryQuery {
  entityId?: string;
  issueDateFrom: string;
  issueDateTo: string;
}

export interface ReceivedNfAddressingSummaryResponse {
  totalReceived: number;
  addressedCount: number;
}

/**
 * Inbox document types the operator can transmute into an `ap_bill` by hand.
 *
 * Single source of truth for the API guards (`enqueueBillProcessing`,
 * `recreateBill`) and the Received NFs page action, which used to carry four
 * independent copies of the same array — CFDI support had to be added in four
 * places or it silently worked in one and 400ed in another.
 */
export const MANUAL_BILL_PROCESSABLE_INBOX_DOCUMENT_TYPES = [
  'NFE',
  'NFSE_NACIONAL',
  'CFDI',
] as const satisfies readonly InboxDocumentType[];

/**
 * Inbox document types the *automatic* sweep (`enqueueAllPendingBillProcessing`
 * and the Focus scheduler) may pick up on its own.
 *
 * CFDI is deliberately absent, and this list is deliberately NOT the manual one.
 * The CFDI bill pipeline is new: a payload the mapper cannot make sense of (no
 * `Moneda`, an unexpected `TipoDeComprobante`, taxes in a shape we have not seen
 * yet) fails its job, and BullMQ retries it. On the manual path a human sees the
 * failure and stops; on the automatic sweep every pending CFDI in the inbox would
 * be re-enqueued each cycle, so one bad payload shape becomes a retry loop across
 * the whole tenant. CFDI joins this list once the pipeline has run manually long
 * enough to know which payload shapes actually arrive.
 */
export const AUTO_BILL_PROCESSABLE_INBOX_DOCUMENT_TYPES = [
  'NFE',
  'NFSE_NACIONAL',
] as const satisfies readonly InboxDocumentType[];

/** True when an operator may transmute this inbox document type by hand. */
export function isManualBillProcessableDocumentType(
  documentType: InboxDocumentType,
): documentType is (typeof MANUAL_BILL_PROCESSABLE_INBOX_DOCUMENT_TYPES)[number] {
  return (MANUAL_BILL_PROCESSABLE_INBOX_DOCUMENT_TYPES as readonly InboxDocumentType[]).includes(
    documentType,
  );
}

/** True when the automatic sweep may transmute this inbox document type. */
export function isAutoBillProcessableDocumentType(
  documentType: InboxDocumentType,
): documentType is (typeof AUTO_BILL_PROCESSABLE_INBOX_DOCUMENT_TYPES)[number] {
  return (AUTO_BILL_PROCESSABLE_INBOX_DOCUMENT_TYPES as readonly InboxDocumentType[]).includes(
    documentType,
  );
}
